import {
  type ComponentProps,
  createElement,
  type ForwardedRef,
  forwardRef,
  type JSX,
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
} from "react";
import { Link } from "react-router";

import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { Spinner } from "../Loaders/LoadingSpinner/Spinner";
import { withTooltip } from "../Tooltip/withTooltip";

export type ThemedButtonProps = ComponentProps<"button"> & {
  ariaLabel?: string;
  as?: keyof JSX.IntrinsicElements;
  children?: ReactNode;
  className?: string;
  isCompact?: boolean;
  dataTestId: string;
  disabled?: boolean;
  fullWidth?: boolean;
  href?: string;
  htmlFor?: string;
  icon?: ReactNode;
  iconContainerStyles?: string;
  iconPosition?: "left" | "right";
  isLoading?: boolean;
  onClick?: MouseEventHandler<HTMLElement>;
  rel?: string;
  role?: "button" | "reset" | "submit";
  route?: string;
  showScreenBlocker?: boolean;
  size?: "default" | "small";
  target?: string;
  tooltip?: ReactNode | string;
  truncate?: boolean;
  variant?:
    | "destructive"
    | "input"
    | "primary"
    | "secondary"
    | "tertiary"
    | "text";
};

export const BaseThemedButton = (
  {
    ariaLabel,
    as = "button",
    children,
    className,
    isCompact = false,
    dataTestId,
    disabled,
    fullWidth = false,
    htmlFor,
    icon,
    iconContainerStyles,
    iconPosition = "left",
    isLoading,
    onClick,
    rel,
    role,
    route,
    showScreenBlocker = false,
    size = "default",
    target,
    truncate = false,
    variant = "primary",
    ...props
  }: ThemedButtonProps,
  ref: ForwardedRef<HTMLElement>,
) => {
  const baseStyles =
    "inline-flex items-center justify-center h-fit min-h-11 py-1 rounded-default text-nowrap defaultTransition px-5 body-base-semibold outline-none";

  const variantStyles = {
    destructive: `
      shadow-card
      hover:shadow-tooltip
      active:shadow-none
      disabled:shadow-none

      bg-button/destructive/background/default
      text-button/destructive/label/default
      border border-button/destructive/border/default
      hover:bg-button/destructive/background/hover
      hover:border-button/destructive/border/hover
      active:bg-button/destructive/background/pressed
      active:border-button/destructive/border/pressed
      disabled:bg-background/brand/opacity
      disabled:text-text/disabled
      disabled:border-background/brand/opacity
      focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-default disabled:focus-visible:ring-0
    `,
    primary: `
      shadow-card
      hover:shadow-tooltip
      active:shadow-none
      disabled:shadow-none

      bg-button/primary/background/default
      text-button/primary/label/default
      hover:bg-button/primary/background/hover
      active:bg-button/primary/background/pressed
      disabled:bg-button/primary/background/default/50
      disabled:text-button/primary/label/default
      focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-active disabled:focus-visible:ring-0
    `,
    secondary: `
      shadow-card
      hover:shadow-tooltip
      active:shadow-none
      disabled:shadow-none

      bg-button/secondary/background/default
      text-button/secondary/label/default
      border border-button/secondary/border/default
      hover:bg-button/secondary/background/hover
      hover:border-button/secondary/border/hover
      active:bg-button/secondary/background/pressed
      active:border-button/secondary/border/pressed
      disabled:text-text/disabled
      disabled:border-background/brand/opacity
      disabled:hover:bg-button/secondary/background/default
      focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-default disabled:focus-visible:ring-0
    `,
    tertiary: `
      text-button/tertiary/foreground/default
      hover:text-button/tertiary/foreground/hover
      active:text-button/tertiary/foreground/pressed
      disabled:text-text/disabled
      disabled:hover:text-text/disabled
      focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-default disabled:focus-visible:ring-0
    `,
    input: `
      px-2
      hover:shadow-tooltip
      active:shadow-none
      disabled:shadow-none

      border border-button/secondary/border/default
      active:bg-button/secondary/background/pressed
      disabled:border-background/brand/opacity
      disabled:bg-background/component/10
      bg-background/primary
      text-text/primary
      hover:text-text/primary
      hover:bg-background/primary
      hover:not-disabled:border-brand-active
      active:border-brand-active
      focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-default disabled:focus-visible:ring-0

      body-base-regular
    `,
    text: `
      text-text/secondary underline hover:text-text/interactive active:text-text/interactive disabled:text-text/disabled disabled:hover:text-text/disabled p-0 inline-flex min-h-1
      focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-default disabled:focus-visible:ring-0
    `,
  };

  const fullWidthStyles = fullWidth ? "w-full" : "w-min";
  const iconOnly = !children && isTruthy(icon);

  const buttonContent = (
    <span className="relative flex items-center justify-center w-full">
      {isTruthy(isLoading) && (
        <span className="absolute inset-0 flex items-center justify-center h-full w-full select-none cursor-wait user-select-none">
          <Spinner asIcon />
          {showScreenBlocker ? <ScreenBlocker /> : null}
        </span>
      )}
      <span
        className={cnTwMerge("flex items-center", {
          "w-full": variant === "input",
          "opacity-0": isLoading,
          "gap-2": isTruthy(icon) && children,
          "truncate block": truncate && !isTruthy(icon),
          "overflow-hidden": truncate && isTruthy(icon),
        })}
      >
        {isTruthy(icon) && iconPosition === "left" && (
          <span className={cnTwMerge(iconContainerStyles)}>{icon}</span>
        )}

        {truncate && isTruthy(icon) ? (
          <span className="truncate">{children}</span>
        ) : (
          children
        )}

        {isTruthy(icon) && iconPosition === "right" && (
          <span className={cnTwMerge(iconContainerStyles)}>{icon}</span>
        )}
      </span>
    </span>
  );

  const compactStyles = isCompact ? "min-h-8 px-3 body-small-semibold" : "";

  const classNameCombined = cnTwMerge(
    baseStyles,
    variantStyles[variant],
    compactStyles,
    fullWidthStyles,
    {
      "cursor-not-allowed select-none user-select-none": isTruthy(disabled),
      "cursor-wait select-none user-select-none": isTruthy(isLoading),
      "p-2": iconOnly,
    },
    className,
  );

  const checkAndClick = (event: MouseEvent<HTMLElement>) => {
    if (isFalsy(disabled) && isFalsy(isLoading)) {
      onClick && onClick(event);
    } else if (!onClick) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  if (isTruthy(route)) {
    return (
      <Link
        className={classNameCombined}
        data-testid={dataTestId}
        onClick={checkAndClick}
        rel={rel}
        target={target}
        to={route}
      >
        {buttonContent}
      </Link>
    );
  }

  if (variant === "text") {
    return (
      <button
        aria-label={ariaLabel}
        className={cnTwMerge(classNameCombined, {
          "body-small-regular": size === "small",
        })}
        data-testid={dataTestId}
        onClick={checkAndClick}
        ref={ref as Ref<HTMLButtonElement>}
        tabIndex={0}
        // eslint-disable-next-line react/button-has-type
        type={props.type ?? "button"}
        {...props}
      >
        {buttonContent}
      </button>
    );
  }

  return createElement(
    as,
    {
      "aria-label": ariaLabel,
      className: classNameCombined,
      disabled: disabled ?? isLoading,
      htmlFor,
      onClick: checkAndClick,
      ref,
      rel,
      role: role ?? "button",
      tabIndex: 0,
      target,
      ["data-testid"]: dataTestId,
      ...props,
    },
    buttonContent,
  );
};

export const ThemedButton = withTooltip(
  forwardRef<HTMLElement, ThemedButtonProps>(BaseThemedButton),
);

function ScreenBlocker() {
  return (
    <div className="z-[101] items-center overflow-y-auto fixed inset-0 h-full w-full" />
  );
}
